import { httpClient } from '@bs/core'
import type { WHLocation } from '../../types/warehouse'

function mapFromApi(item: WHLocation): WHLocation {
  return {
    ...item,
    warehouse_id: item._id,
    company_id:
      item.cova_metadata?.CompanyId?.toString() ||
      item.greenline_mapping?.company_id ||
      item.pos_mapping?.company_id,
    id:
      item.cova_metadata?.LocationId?.toString() ||
      item.greenline_mapping?.location_id ||
      item.pos_mapping?.location_id,
  }
}

export interface WHLocationPaginateParams {
  storeId: string
  organizationId?: string
  page: number
  limit: number
  search?: string
}

export interface WHLocationPaginateResult {
  items: WHLocation[]
  page: number
  limit: number
  offset: number
  totalItems: number
  totalPages: number
  totalPosConnected: number
  totalVisible: number
  hasSyncingLocation: boolean
}

// `httpClient.json.*` auto-unwraps the `{ status, message, data }` envelope and
// returns the inner `data` payload directly, so this shape matches what the
// gateway actually receives (not the raw HTTP body).
interface WHLocationPaginateApiResponse {
  items?: WHLocation[]
  page?: number
  limit?: number
  offset?: number
  total_items?: number
  total_pages?: number
  total_pos_connected?: number
  total_visible?: number
  has_syncing_location?: boolean
}

export const warehouseApis = {
  getList: async (
    storeId: string,
    organizationId?: string,
  ): Promise<WHLocation[]> => {
    const params: Record<string, string> = { store_id: storeId }
    if (organizationId) params.organization_id = organizationId
    const raw = (await httpClient.json.get(
      'organizations/stores/warehouses/protected',
      {
        params,
      },
    )) as WHLocation[]
    return (raw ?? []).map(mapFromApi)
  },

  // Paginated location list that returns ALL warehouses (including incomplete drafts the
  // `/protected` endpoint hides). Used by the Settings → Locations list view.
  // Backed by the lean `warehouses/pantry-locations/paginate` endpoint which
  // projects only the fields the list renders and includes store-wide aggregate
  // counts (`total_pos_connected`, `total_visible`) for the stat cards.
  paginate: async (
    input: WHLocationPaginateParams,
  ): Promise<WHLocationPaginateResult> => {
    const params: Record<string, string | number> = {
      store_id: input.storeId,
      page: input.page,
      limit: input.limit,
    }
    if (input.organizationId) params.organization_id = input.organizationId
    if (input.search && input.search.trim()) params.search = input.search.trim()

    const raw = (await httpClient.json.get(
      'warehouses/pantry-locations/paginate',
      {
        params,
      },
    )) as WHLocationPaginateApiResponse

    // Defensive: the gateway accepts either the auto-unwrapped `data` payload
    // (`{ items, page, … }`) or — in case `httpClient` ever stops unwrapping —
    // the full envelope (`{ data: { items, … } }`).
    const data =
      raw && Array.isArray((raw as WHLocationPaginateApiResponse).items)
        ? raw
        : ((raw as unknown as { data?: WHLocationPaginateApiResponse })?.data ??
          {})

    return {
      items: (data.items ?? []).map(mapFromApi),
      page: data.page ?? input.page,
      limit: data.limit ?? input.limit,
      offset: data.offset ?? 0,
      totalItems: data.total_items ?? 0,
      totalPages: data.total_pages ?? 0,
      totalPosConnected: data.total_pos_connected ?? 0,
      totalVisible: data.total_visible ?? 0,
      hasSyncingLocation: data.has_syncing_location ?? false,
    }
  },
}
